// src/pages/MyCheckout.jsx
//
// Student-only read-only view of THEIR OWN checkout sheet.
//
// Access model:
//   - Only students reach this route (StudentRoute gate in App.js).
//   - Subscribes to /checkoutRecords/{currentSchoolYear}_{theirSisId}.
//     If Firestore rules deny — or the doc doesn't exist yet — we show
//     a "not yet" empty state.
//
// What students see:
//   - Each section's status (Pending / Signed / N/A)
//   - Teacher names (per-section, multi-teacher aware)
//   - Free-text context (which book / device / sport)
//   - Who signed off (signedByName) for completed sections
//
// What students DO NOT see:
//   - The free-form `notes` field staff write when signing off
//   - The `damageNotes` field on dorm sections
//   - Other students' records (security rules block)
//   - Faculty data, signoff history, signers lists, etc.

import React, { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import {
  CHECKOUT_SECTIONS,
  SECTION_STATUS,
  KA_COLORS,
  getRelevantSectionIds,
  computeCompletionPercent,
  formatSignoffTimestamp,
  AUTO_SECTION_IDS,
} from '../utils/checkoutHelpers';

// -----------------------------------------------------------------------------
// Styles

const styles = {
  page: {
    maxWidth: 800,
    margin: '0 auto',
    padding: '24px 20px 48px',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    color: '#222',
  },
  header: {
    background: KA_COLORS.navy,
    color: '#fff',
    padding: '24px 28px',
    borderRadius: 8,
    marginBottom: 24,
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  },
  schoolName: {
    fontFamily: 'Georgia, serif',
    fontSize: 22,
    fontWeight: 700,
    margin: 0,
  },
  schoolSub: {
    margin: '4px 0 0',
    fontSize: 13,
    opacity: 0.85,
  },
  pageTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 26,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: '0 0 4px',
  },
  pageSub: {
    color: '#666',
    fontSize: 14,
    margin: '0 0 20px',
  },
  studentCard: {
    background: '#fff',
    border: `1px solid #E0E0E0`,
    borderRadius: 6,
    padding: '18px 22px',
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    flexWrap: 'wrap',
  },
  studentName: {
    fontFamily: 'Georgia, serif',
    fontSize: 20,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: 0,
  },
  studentMeta: {
    fontSize: 13,
    color: '#666',
    margin: '4px 0 0',
  },
  progressBox: {
    minWidth: 200,
  },
  progressLabel: {
    fontSize: 11,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 600,
  },
  progressVal: {
    fontFamily: 'Georgia, serif',
    fontSize: 24,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: '2px 0 6px',
  },
  progressBar: {
    height: 8,
    background: '#E0E0E0',
    borderRadius: 4,
    overflow: 'hidden',
  },
  progressFill: (pct, complete) => ({
    width: `${pct}%`,
    height: '100%',
    background: complete ? '#2E7D32' : KA_COLORS.gold,
    transition: 'width 0.3s ease',
  }),
  emptyState: {
    background: '#fff',
    border: '1px dashed #BDBDBD',
    borderRadius: 6,
    padding: '36px 28px',
    textAlign: 'center',
  },
  emptyIcon: {
    fontSize: 40,
    marginBottom: 8,
  },
  emptyTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 18,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: '0 0 6px',
  },
  emptyText: {
    color: '#555',
    fontSize: 14,
    lineHeight: 1.5,
    margin: 0,
  },
  sectionsHeader: {
    fontFamily: 'Georgia, serif',
    fontSize: 18,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: '0 0 8px',
  },
  legend: {
    display: 'flex',
    gap: 18,
    fontSize: 12,
    color: '#666',
    marginBottom: 14,
    flexWrap: 'wrap',
  },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6 },
  sectionCard: {
    background: '#fff',
    border: '1px solid #E0E0E0',
    borderRadius: 6,
    padding: '16px 18px',
    marginBottom: 10,
  },
  sectionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
    alignItems: 'flex-start',
  },
  sectionMain: { flex: 1, minWidth: 0 },
  sectionLabel: {
    fontFamily: 'Georgia, serif',
    fontSize: 16,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: 0,
  },
  sectionDesc: {
    fontSize: 13,
    color: '#666',
    margin: '2px 0 0',
    lineHeight: 1.4,
  },
  contextLine: {
    fontSize: 13,
    color: '#222',
    margin: '8px 0 0',
    lineHeight: 1.4,
    background: '#FFF8E1',
    border: '1px solid #FFE082',
    padding: '6px 10px',
    borderRadius: 4,
  },
  contextLabel: { fontWeight: 600, color: '#7A5C00' },
  teacherCountLine: {
    fontSize: 13,
    color: KA_COLORS.navy,
    margin: '8px 0 4px',
  },
  teacherList: {
    margin: '4px 0 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  teacherRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 12,
    padding: '8px 10px',
    background: '#FAFAFA',
    border: '1px solid #E0E0E0',
    borderRadius: 4,
  },
  teacherRowName: {
    fontSize: 13,
    fontWeight: 600,
    color: KA_COLORS.navy,
  },
  teacherRowState: { fontSize: 12 },
  signoffInfo: {
    fontSize: 13,
    color: '#444',
    marginTop: 8,
    lineHeight: 1.5,
  },
  statusPill: (status) => {
    const palette = {
      [SECTION_STATUS.SIGNED]: { bg: '#D4EDDA', fg: '#155724' },
      [SECTION_STATUS.PENDING]: { bg: '#FFF3CD', fg: '#856404' },
      [SECTION_STATUS.NA]: { bg: '#E9ECEF', fg: '#6C757D' },
    };
    const p = palette[status] || palette[SECTION_STATUS.PENDING];
    return {
      display: 'inline-block',
      padding: '4px 10px',
      borderRadius: 12,
      background: p.bg,
      color: p.fg,
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
    };
  },
  legendDot: (color) => ({
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: color,
    display: 'inline-block',
  }),
  signOutBtn: {
    background: '#fff',
    border: '1px solid #999',
    borderRadius: 4,
    padding: '6px 12px',
    fontSize: 12,
    color: '#444',
    cursor: 'pointer',
    fontWeight: 600,
  },
  topBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  loading: {
    textAlign: 'center',
    color: '#666',
    fontStyle: 'italic',
    padding: 40,
  },
};

const getStatusLabel = (status) => {
  switch (status) {
    case SECTION_STATUS.SIGNED:
      return 'Signed';
    case SECTION_STATUS.NA:
      return 'N/A';
    case SECTION_STATUS.PENDING:
    default:
      return 'Pending';
  }
};

// -----------------------------------------------------------------------------
// Student-facing section card. Always read-only; intentionally hides
// internal notes / damageNotes / signoffHistory.

const StudentSectionCard = ({ section, data }) => {
  const status = data?.status || SECTION_STATUS.PENDING;
  const hasTeachers =
    Array.isArray(data?.teachers) && data.teachers.length > 0;

  const signedCount = hasTeachers
    ? data.teachers.filter((t) => !!t.signedAt).length
    : 0;
  const totalTeachers = hasTeachers ? data.teachers.length : 0;

  return (
    <div style={styles.sectionCard}>
      <div style={styles.sectionRow}>
        <div style={styles.sectionMain}>
          <p style={styles.sectionLabel}>{section.label}</p>
          {section.description && (
            <p style={styles.sectionDesc}>{section.description}</p>
          )}

          {data?.context && (
            <p style={styles.contextLine}>
              <span style={styles.contextLabel}>For:</span> {data.context}
            </p>
          )}

          {hasTeachers && (
            <>
              <p style={styles.teacherCountLine}>
                <strong>Teachers:</strong> {signedCount} of {totalTeachers}{' '}
                cleared
              </p>
              <div style={styles.teacherList}>
                {data.teachers.map((t, idx) => {
                  const slotKey = `${section.id}-${t.sisId || idx}`;
                  return (
                    <div key={slotKey} style={styles.teacherRow}>
                      <span style={styles.teacherRowName}>
                        {t.name || '(unnamed)'}
                      </span>
                      <span
                        style={{
                          ...styles.teacherRowState,
                          color: t.signedAt ? '#1B5E20' : '#996515',
                          fontWeight: t.signedAt ? 'normal' : 600,
                        }}
                      >
                        {t.signedAt ? (
                          <>
                            ✓ Signed by{' '}
                            <strong>{t.signedByName || 'Unknown'}</strong>
                            {t.signedAt && (
                              <>
                                {' '}
                                · {formatSignoffTimestamp(t.signedAt)}
                              </>
                            )}
                          </>
                        ) : (
                          'Pending'
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* For single-signer sections (no teachers[] array), show
              section-level signoff info if signed. */}
          {!hasTeachers && status === SECTION_STATUS.SIGNED && (
            <div style={styles.signoffInfo}>
              ✓ Signed off by{' '}
              <strong style={{ color: KA_COLORS.navy }}>
                {data.signedByName || 'Unknown'}
              </strong>
              {data.signedAt && (
                <> &nbsp;·&nbsp; {formatSignoffTimestamp(data.signedAt)}</>
              )}
            </div>
          )}
        </div>

        <div>
          <span style={styles.statusPill(status)}>
            {getStatusLabel(status)}
          </span>
        </div>
      </div>
    </div>
  );
};

// -----------------------------------------------------------------------------

export default function MyCheckout() {
  const { user, profile, studentSisId, logout } = useAuth();
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // The current school year. We could read /config/systemSettings, but
  // students shouldn't have read access to /config/* (security rules will
  // deny). The simplest path: try the current calendar-year inferred school
  // year first (works for the typical case), and fall back to checking the
  // student's record id directly. For now, hardcode '2025-2026' — this
  // matches the rest of the app's DEFAULT_SCHOOL_YEAR. If you change the
  // school year mid-application, update this.
  //
  // (Better long-term: stamp currentSchoolYear onto the user doc when
  // ensureStudentProfileDoc runs, or onto the /students/{sisId} doc.)
  const schoolYear = '2025-2026';

  useEffect(() => {
    if (!user || !studentSisId) {
      setLoading(false);
      return undefined;
    }
    const recordId = `${schoolYear}_${studentSisId}`;
    const ref = doc(db, 'checkoutRecords', recordId);

    setLoading(true);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          setRecord({ id: snap.id, ...snap.data() });
        } else {
          setRecord(null);
        }
        setLoading(false);
      },
      (err) => {
        // Firestore permission errors land here — we show the empty state
        // rather than leaking that something exists. Same UX as "not
        // created yet" so we don't reveal record existence.
        console.error('MyCheckout subscription error:', err);
        setRecord(null);
        setLoading(false);
        setError(err.code === 'permission-denied' ? null : err.message);
      },
    );
    return unsub;
  }, [user, studentSisId, schoolYear]);

  const completion = record ? computeCompletionPercent(record) : 0;
  const relevantIds = record ? getRelevantSectionIds(record) : [];

  return (
    <div style={styles.page}>
      <div style={styles.topBar}>
        <div>
          <p style={{ margin: 0, fontSize: 12, color: '#666' }}>
            Signed in as <strong>{profile?.displayName || user?.email}</strong>
          </p>
        </div>
        <button type="button" style={styles.signOutBtn} onClick={logout}>
          Sign out
        </button>
      </div>

      <div style={styles.header}>
        <h1 style={styles.schoolName}>King's Academy</h1>
        <p style={styles.schoolSub}>Madaba, Jordan · End-of-Year Checkout</p>
      </div>

      <h2 style={styles.pageTitle}>Your checkout</h2>
      <p style={styles.pageSub}>
        Track your end-of-year clearance progress. Each section below shows
        whether you still need to return materials or get a signoff. Visit
        the relevant teacher or office for any pending items.
      </p>

      {loading && <div style={styles.loading}>Loading your checkout sheet…</div>}

      {!loading && !record && (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📋</div>
          <p style={styles.emptyTitle}>
            Your checkout sheet hasn't been created yet
          </p>
          <p style={styles.emptyText}>
            It will be available soon. If you think this is an error, please
            contact your advisor or the school office.
          </p>
        </div>
      )}

      {!loading && record && (
        <>
          <div style={styles.studentCard}>
            <div>
              <h3 style={styles.studentName}>
                {record.studentName || 'Student'}
              </h3>
              <p style={styles.studentMeta}>
                {[record.grade, record.cohort].filter(Boolean).join(' · ')}
                {record.schoolYear ? ` · ${record.schoolYear}` : ''}
              </p>
            </div>
            <div style={styles.progressBox}>
              <div style={styles.progressLabel}>Progress</div>
              <div style={styles.progressVal}>{completion}%</div>
              <div style={styles.progressBar}>
                <div style={styles.progressFill(completion, completion === 100)} />
              </div>
            </div>
          </div>

          <h3 style={styles.sectionsHeader}>Sections</h3>
          <div style={styles.legend}>
            <div style={styles.legendItem}>
              <span style={styles.legendDot('#FFF3CD')} /> Pending
            </div>
            <div style={styles.legendItem}>
              <span style={styles.legendDot('#D4EDDA')} /> Signed
            </div>
            <div style={styles.legendItem}>
              <span style={styles.legendDot('#E9ECEF')} /> N/A — not required
              for you
            </div>
          </div>

          {/* Show only sections that are relevant to this student
              (i.e. status !== 'na'). N/A sections are hidden entirely
              from the student view since they're not applicable. */}
          {CHECKOUT_SECTIONS.filter((s) => relevantIds.includes(s.id)).map(
            (section) => (
              <StudentSectionCard
                key={section.id}
                section={section}
                data={record.sections?.[section.id] || {}}
              />
            ),
          )}

          {relevantIds.length === 0 && (
            <div style={styles.emptyState}>
              <p style={styles.emptyText}>
                No checkout sections are currently assigned to you. Contact
                your advisor if you think this is an error.
              </p>
            </div>
          )}
        </>
      )}

      {error && (
        <div
          style={{
            marginTop: 16,
            padding: 12,
            background: '#FEEBE9',
            border: `1px solid ${KA_COLORS.red}`,
            borderRadius: 4,
            color: KA_COLORS.red,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}